import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, Globe, Mail, Phone, Package } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, FormDialog, EmptyState, clientTitle, clientSub } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { ClientLogo, useClientBrands } from "@/components/ClientBrand";

const FIELDS = [
  { name: "brand_name", label: "Brand name", required: true, full: true },
  { name: "name", label: "Contact name", required: true }, { name: "company", label: "Company" },
  { name: "email", label: "Email", type: "email" }, { name: "phone", label: "Phone" },
  { name: "website", label: "Website" }, { name: "industry", label: "Industry" },
  { name: "status", label: "Status", type: "select", options: [{ value: "lead", label: "Lead" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
  { name: "notes", label: "Notes", type: "textarea", full: true },
];
const PORTAL_FIELDS = [{ name: "name", label: "Contact name", required: true, full: true }, { name: "email", label: "Login email", type: "email", required: true, full: true }, { name: "password", label: "Temporary password", type: "password", required: true, full: true }];

export default function Clients() {
  const { user } = useAuth();
  const { refresh: refreshBrands } = useClientBrands();
  const isAdmin = user.role === "admin";
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [portal, setPortal] = useState(null);
  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (edit?.id) await api.put(`/clients/${edit.id}`, form); else await api.post("/clients", form);
    toast.success(edit?.id ? "Client updated" : "Client added"); load(); refreshBrands();
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${clientTitle(c)}? Projects remain but become unlinked.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); refreshBrands(); } catch (e) { toast.error(errMsg(e)); }
  };
  const grant = async (form) => { await api.post(`/clients/${portal.id}/portal-access`, form); toast.success("Portal access granted"); load(); };

  const shown = clients.filter((c) => `${c.brand_name} ${c.name} ${c.company} ${c.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="CRM" title="Clients" subtitle={`${clients.length} accounts · ${clients.filter((c) => c.status === "active").length} active · ${clients.filter((c) => c.status === "lead").length} leads`}
        actions={<>
          <Input placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} className="w-48 bg-white" data-testid="clients-search-input" />
          {isAdmin && <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setEdit({ status: "lead" })} data-testid="add-client-btn"><Plus size={16} className="mr-1" />Add client</Button>}
        </>} />

      {!shown.length ? <EmptyState title="No clients yet" hint="Add your first client to start quoting and running projects." testId="clients-empty" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((c, i) => (
            <div key={c.id} className="panel panel-hover p-5 flex flex-col fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <ClientLogo client={c} size={40} testId={`client-card-logo-${c.id}`} />
                <div className="min-w-0 flex-1"><Link to={`/clients/${c.id}`} className="font-heading font-semibold text-slate-900 truncate block hover:text-blue-600" data-testid={`client-link-${c.id}`}>{clientTitle(c)}</Link><div className="text-sm text-slate-500 truncate" data-testid={`client-sub-${c.id}`}>{clientSub(c) || "—"}</div></div>
                <StatusBadge status={c.status} testId={`client-status-${c.id}`} />
              </div>
              <div className="mt-4 space-y-1.5 text-xs text-slate-600">
                {c.email && <div className="flex items-center gap-2 truncate"><Mail size={12} className="text-slate-400" />{c.email}</div>}
                {c.phone && <div className="flex items-center gap-2"><Phone size={12} className="text-slate-400" />{c.phone}</div>}
                {c.website && <div className="flex items-center gap-2 truncate"><Globe size={12} className="text-slate-400" />{c.website}</div>}
              </div>
              {c.notes && <p className="text-xs text-slate-500 mt-3 line-clamp-2">{c.notes}</p>}
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                <div className="text-slate-600"><span className="num font-semibold text-slate-900">{c.active_projects}</span> active · <span className="num">{c.project_count}</span> total projects</div>
                <div className={`text-[11px] ${c.portal_email ? "text-emerald-600" : "text-slate-400"}`}>{c.portal_email ? "Portal on" : "No portal"}</div>
              </div>
              {isAdmin && (
                <div className="mt-3 flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil size={13} className="mr-1" />Edit</Button>
                  <Link to={`/clients/${c.id}`}><Button size="sm" variant="outline" data-testid={`open-client-${c.id}`}><Package size={13} className="mr-1" />Workspace</Button></Link>
                  <Button size="sm" variant="outline" onClick={() => setPortal(c)} data-testid={`portal-access-${c.id}`}><KeyRound size={13} className="mr-1" />{c.portal_email ? "Reset portal" : "Portal access"}</Button>
                  <Button size="sm" variant="ghost" className="ml-auto text-slate-400 hover:text-rose-600" onClick={() => remove(c)} data-testid={`delete-client-${c.id}`}><Trash2 size={14} /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit client" : "New client"} fields={FIELDS} initial={edit || {}} onSubmit={save} testId="client-dialog" />
      <FormDialog open={!!portal} onOpenChange={(o) => !o && setPortal(null)} title={`Portal access · ${clientTitle(portal)}`} description="Creates a client login that can see this client's projects, proposals, invoices and files." fields={PORTAL_FIELDS} initial={{ name: portal?.name, email: portal?.portal_email || portal?.email }} onSubmit={grant} submitLabel="Grant access" testId="portal-dialog" />
    </div>
  );
}
